// frontend/src/components/AdminPage.js
import { useEffect, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { signOut } from "@/lib/auth";
import { collectPublishErrors } from "@/lib/publishRules";
import { fetchDraft, saveDraft, publish } from "@/lib/siteConfigStore";
import { SITE_SLUG } from "@/config/defaultSiteConfig";
import {
  draftPreviewHref,
  ORG_SLUG,
  PUBLIC_SITES,
  publishedStudioHref,
  siteSupportsStartHere,
} from "@/config/siteRegistry";
import ContentTab from "@/components/ContentTab";
import PartnersTab from "@/components/PartnersTab";
import ColorsTab from "@/components/ColorsTab";
import TypographyTab from "@/components/TypographyTab";
import ProductsTab from "@/components/ProductsTab";
import StartHereTab from "@/components/StartHereTab";
import LayoutTab from "@/components/LayoutTab";
import StoresTab from "@/components/StoresTab";

const TABS = ["Content", "Products", "Start Here", "Stores", "Partners", "Layout", "Colors", "Typography"];

function AdminPage() {
  const [siteSlug, setSiteSlug] = useState(SITE_SLUG);
  const [config, setConfig] = useState(null);
  const [orgConfig, setOrgConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("Content");
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [loadError, setLoadError] = useState(null);
  const [siteSwitching, setSiteSwitching] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);
  const statusTimerRef = useRef(null);

  useEffect(() => {
    fetchDraft(SITE_SLUG)
      .then((result) => setConfig(result.draft))
      .catch((err) => {
        console.error("Failed to load site draft:", err);
        setLoadError(err.message);
      });

    fetchDraft(ORG_SLUG)
      .then((result) => setOrgConfig(result.draft))
      .catch((err) => {
        console.error("Failed to load org draft:", err);
        setLoadError(err.message);
      });
  }, []);

  const setSaveStateWithTimer = (state, delayMs) => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setSaveState(state);
    if (delayMs) {
      statusTimerRef.current = setTimeout(() => setSaveState("idle"), delayMs);
    }
  };

  const handleSaveDraft = async () => {
    setSaveStateWithTimer("saving");
    try {
      await saveDraft(ORG_SLUG, orgConfig);
      await saveDraft(siteSlug, config);
      setPreviewVersion((value) => value + 1);
      setSaveStateWithTimer("saved", 2000);
    } catch (err) {
      console.error("Save failed:", err);
      setSaveStateWithTimer("error", 3000);
    }
  };

  const handlePublish = async () => {
    // All publish-blocking rules live in collectPublishErrors (lib/publishRules)
    // so the gate wiring is pinned by tests — add new rules there, not here.
    const errors = collectPublishErrors(config, orgConfig);
    if (errors.length) {
      errors.forEach((e) => toast.error(e));
      setSaveStateWithTimer("publish_error", 3000);
      return;
    }
    setSaveStateWithTimer("saving");
    try {
      await saveDraft(ORG_SLUG, orgConfig);
      await saveDraft(siteSlug, config);
      await publish(ORG_SLUG);
      await publish(siteSlug);
      setPreviewVersion((value) => value + 1);
      setSaveStateWithTimer("saved", 2000);
    } catch (err) {
      console.error("Publish failed:", err);
      setSaveStateWithTimer("publish_error", 3000);
    }
  };

  const handleSiteChange = async (e) => {
    const nextSlug = e.target.value;
    if (nextSlug === siteSlug) return;
    setSiteSwitching(true);
    try {
      const result = await fetchDraft(nextSlug);
      setSiteSlug(nextSlug);
      setConfig(result.draft);
      setPreviewVersion((value) => value + 1);
      if (activeTab === "Start Here" && !siteSupportsStartHere(nextSlug)) {
        setActiveTab("Content");
      }
    } catch (err) {
      console.error("Failed to load site draft:", err);
      toast.error(err.message || "Failed to load site draft");
    } finally {
      setSiteSwitching(false);
    }
  };

  const showStartHereTab = siteSupportsStartHere(siteSlug);
  const visibleTabs = showStartHereTab ? TABS : TABS.filter((tab) => tab !== "Start Here");

  // The embedded pane always renders the same-origin draft preview — a
  // cross-origin iframe can never authenticate (the admin session does not
  // travel; storage partitioning blocks it), so it would silently show the
  // wrong thing. See siteRegistry.draftPreviewHref.
  const previewHref = draftPreviewHref(siteSlug);
  const previewSrc = `${previewHref}${previewHref.includes("?") ? "&" : "?"}v=${previewVersion}`;
  const studioHref =
    siteSlug === "578-studio" ? publishedStudioHref(process.env.REACT_APP_STUDIO_ORIGIN || "") : "";
  const fullPreviewHref = studioHref || previewHref;
  const fullPreviewLabel = studioHref ? "Open Full Preview (published site)" : "Open Full Preview";
  const selectedSiteLabel = PUBLIC_SITES.find((site) => site.slug === siteSlug)?.label || siteSlug;

  const statusText = {
    idle: "Draft",
    saving: "Saving...",
    saved: "Saved",
    error: "Save failed",
    publish_error: "Publish failed",
  }[saveState];

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl text-[#3A3530] mb-4" style={{ fontFamily: "Federo, serif" }}>
            Failed to load draft
          </h1>
          <p className="text-[#3A3530] mb-2">{loadError}</p>
          <p className="text-sm text-[#3A3530] mb-6">Check your connection and Supabase configuration.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1E1E1E] text-[#F5EDE0] px-4 py-2 text-sm leadline-border-thin"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!config || !orgConfig) {
    return (
      <div className="min-h-screen bg-[#F9F5EE] flex items-center justify-center">
        <p className="text-[#3A3530]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F5EE] text-[#3A3530] pb-10">
      <Toaster position="top-right" richColors />
      <header className="bg-[#1E1E1E] text-[#F5EDE0] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-base sm:text-lg shrink-0" style={{ fontFamily: "Federo, serif" }}>
              578 Site Admin
            </h1>
            <select
              value={siteSlug}
              onChange={handleSiteChange}
              disabled={siteSwitching || saveState === "saving"}
              className="leadline-border-thin px-2 py-1 text-xs bg-[#F5EDE0] text-[#1E1E1E] max-w-[10rem] sm:max-w-none"
            >
              {PUBLIC_SITES.map((site) => (
                <option key={site.slug} value={site.slug}>
                  {site.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={previewHref}
              target="_blank"
              rel="noopener noreferrer"
              className="leadline-border-thin px-3 py-1 text-xs bg-[#F5EDE0] text-[#1E1E1E]"
            >
              Draft Preview
            </a>
            <button
              onClick={handleSaveDraft}
              disabled={siteSwitching || saveState === "saving"}
              className="leadline-border-thin px-3 py-1 text-xs bg-[#E8A33A] text-[#1E1E1E] disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={handlePublish}
              disabled={siteSwitching || saveState === "saving"}
              className="leadline-border-thin px-3 py-1 text-xs bg-[#2B7D6E] text-[#F5EDE0] disabled:opacity-50"
            >
              Publish
            </button>
            <button
              onClick={() => signOut()}
              className="px-3 py-1 text-xs text-[#F5EDE0] hover:underline"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pt-6">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,28rem)]">
          <section>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {visibleTabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`leadline-border-thin px-3 py-1 text-xs ${
                    activeTab === tab ? "bg-[#1E1E1E] text-[#F5EDE0]" : "bg-[#F5EDE0] text-[#3A3530]"
                  }`}
                >
                  {tab}
                </button>
              ))}
              <span className="ml-auto text-xs">{statusText}</span>
            </div>

            {activeTab === "Content" && <ContentTab config={config} onChange={setConfig} />}
            {activeTab === "Products" && (
              <ProductsTab
                config={config}
                onChange={setConfig}
                orgConfig={orgConfig}
                allowedKinds={config.meta?.allowedKinds || ["digital", "other"]}
              />
            )}
            {activeTab === "Start Here" && showStartHereTab && (
              <StartHereTab config={config} onChange={setConfig} products={config.products || []} />
            )}
            {activeTab === "Stores" && <StoresTab orgConfig={orgConfig} onChange={setOrgConfig} />}
            {activeTab === "Partners" && <PartnersTab config={config} onChange={setConfig} />}
            {activeTab === "Layout" && <LayoutTab config={config} onChange={setConfig} />}
            {activeTab === "Colors" && <ColorsTab config={config} onChange={setConfig} />}
            {activeTab === "Typography" && <TypographyTab config={config} onChange={setConfig} />}
          </section>

          <aside className="lg:sticky lg:top-20 h-fit">
            <div className="leadline-border bg-[#F5EDE0] overflow-hidden">
              <div className="px-4 py-2 bg-[#1E1E1E] text-[#F5EDE0] text-sm flex items-center justify-between gap-2">
                <span>Brand Preview</span>
                <span className="text-xs opacity-80">{selectedSiteLabel}</span>
              </div>
              <div className="p-2 space-y-2">
                <iframe
                  title={`${selectedSiteLabel} draft preview`}
                  src={previewSrc}
                  className="w-full h-[30rem] border border-[#1E1E1E] bg-white"
                />
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setPreviewVersion((value) => value + 1)}
                    className="leadline-border-thin px-2 py-1 text-xs bg-white text-[#3A3530] hover:bg-[#F5EDE0]"
                  >
                    Refresh Pane
                  </button>
                  <a
                    href={fullPreviewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="leadline-border-thin px-2 py-1 text-xs bg-white text-[#3A3530] hover:bg-[#F5EDE0]"
                  >
                    {fullPreviewLabel}
                  </a>
                </div>
                {siteSlug === "578-studio" ? (
                  <p className="text-xs text-[#3A3530] opacity-80">
                    Cross-origin draft preview ships with SNDR-163 (signed preview token).
                  </p>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default AdminPage;
